import React, { useState, useRef, useEffect } from 'react';
import { Plus, Download, Search, Filter, MoreHorizontal, X, Eye, Trash, FileUp, FileDown, Edit, Award, Save, AlertCircle } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { editCertificate, fetchCertificates, importCertificatesCsv, issueCertificate, deleteCertificate } from "../../services/dashboardService";
import { toast } from 'react-toastify';
import api from '@/services/api';

export const Certificates: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewCert, setViewCert] = useState<any | null>(null);
    const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [itemsPerPage] = useState(20);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [certificates, setCertificates] = useState<any[]>([]);
    const [totalCertificates, setTotalCertificates] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [calibrated, setCalibrated] = useState(false);
    const [loadingList, setLoadingList] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    
    // Edit State
    const [editingCertId, setEditingCertId] = useState<number | null>(null);

    // NEW: Delete Confirmation Modal States
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [certToDelete, setCertToDelete] = useState<any | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Filters
    const [statusFilter, setStatusFilter] = useState('All');
    const [courseFilter, setCourseFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const certificateRef = useRef<HTMLDivElement>(null);

    const CERT_BG_URL = '/certificate-bg.jpg';

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        courseOfStudy: '',
        courseSummary: '',
        yearOfStudy: '2025',
        issuanceDate: '',
        certificateNumber: ''
    });

    const buildQrPayload = (certLike: any) => {
        const certificate_number = String(certLike?.certId || certLike?.certificateId || '').trim();
        const verify_url = `https://cert-verify.speedlinkng.com/verify/${encodeURIComponent(certificate_number)}`;
        return verify_url;
    };


    const filteredCertificates = certificates.filter((cert: any) => {
        const q = (searchQuery || '').toLowerCase();
        const name = String(cert?.name || '').toLowerCase();
        const id = String(cert?.certId || '').toLowerCase();
        const course = String(cert?.course || '').toLowerCase();
        const matchesSearch = name.includes(q) || id.includes(q) || course.includes(q);

        const statusVal = String(cert?.status || '');
        const matchesStatus = statusFilter === 'All' || statusVal === statusFilter;
        const matchesCourse = courseFilter === 'All' || cert?.course === courseFilter;
        const dateStr = String(cert?.date || '');
        const matchesDate = dateFilter === '' || dateStr.slice(0, 10) === dateFilter;

        return matchesSearch && matchesStatus && matchesCourse && matchesDate;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCertificates.slice(indexOfFirstItem, indexOfLastItem);
    const totalPagesComputed = Math.max(1, Math.ceil(filteredCertificates.length / itemsPerPage || 1));

    useEffect(() => {
        const loadCertificates = async () => {
            try {
                setLoadingList(true);
                if (certificates.length === 0) {
                    try {
                        const cached = localStorage.getItem('certificates_cache');
                        if (cached) {
                            const items = JSON.parse(cached) || [];
                            const mapped = items.map((c: any) => ({
                                id: c.id,
                                name: c.student_name,
                                course: c.course_name,
                                certId: c.verification_code,
                                date: c.issued_at,
                                verifyUrl: c.verification_url || c.verify_url || undefined,
                                qrImageUrl: c.qr_url || c.qr_code_url || undefined,
                                courseSummary: c.course_summary || '',
                                status: (c.status || c.verification_status || c.state || '').toString(),
                            }));
                            if (mapped.length > 0) setCertificates(mapped);
                        }
                    } catch {}
                }
                const data = await fetchCertificates();
                const mapped = (data.certificates || []).map((c: any) => ({
                    id: c.id,
                    name: c.student_name,
                    course: c.course_name,
                    certId: c.verification_code,
                    date: c.issued_at,
                    verifyUrl: c.verification_url || c.verify_url || undefined,
                    qrImageUrl: c.qr_url || c.qr_code_url || undefined,
                    courseSummary: c.course_summary || '',
                    status: (c.status || c.verification_status || c.state || '').toString(),
                }));
                setCertificates(mapped);
                setTotalCertificates(mapped.length);
                try { localStorage.setItem('certificates_cache', JSON.stringify(data.certificates)); } catch {}
            } catch (err) {
                console.error(err);
                setTotalPages(totalPages || 10);
            }
            finally {
                setLoadingList(false);
            }
        };

        loadCertificates();
    }, []);

    const getFitSize = (t: string, base: number, min: number, max: number) => {
        const W = 606;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let s = base;
        if (!ctx) return Math.min(Math.max(s, min), max);
        while (s > min) {
            ctx.font = `${s}px 'Times New Roman', Times, serif`;
            if (ctx.measureText(t || '').width <= W) break;
            s -= 1;
        }
        if (s < min) s = min;
        if (s > max) s = max;
        return s;
    };
    const getFitSizeNarrow = (t: string, base: number, min: number, max: number) => {
        const W = 260;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let s = base;
        if (!ctx) return Math.min(Math.max(s, min), max);
        while (s > min) {
            ctx.font = `${s}px 'Courier New', monospace`;
            if (ctx.measureText(t || '').width <= W) break;
            s -= 1;
        }
        if (s < min) s = min;
        if (s > max) s = max;
        return s;
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPagesComputed) {
            setCurrentPage(newPage);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveCertificate = async () => {
        if (!formData.firstName || !formData.lastName || !formData.courseOfStudy) {
            alert("Please fill in the required fields.");
            return;
        }

        const d = formData.issuanceDate;
        const today = new Date();
        const isoToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
            .toISOString()
            .split("T")[0];
        const isIso = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d);
        const dd = isIso ? new Date(d) : new Date("invalid");
        const notFuture = isIso && d <= isoToday;
        const notTooOld = isIso && d >= "2000-01-01";

        if (!isIso || isNaN(dd.getTime()) || !notFuture || !notTooOld) {
            alert("Enter a valid issuance date (YYYY-MM-DD), not in the future.");
            return;
        }

        const payload = {
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            course_name: formData.courseOfStudy.trim(),
            course_summary: formData.courseSummary.trim(),
            year_of_study: formData.yearOfStudy,
            issuance_date: formData.issuanceDate
        };

        try {
            let saved;
            if (editingCertId) {
                saved = await editCertificate(editingCertId, payload);
                const updated = {
                    id: saved.id,
                    name: saved.student_name,
                    course: saved.course_name,
                    certId: saved.verification_code,
                    date: saved.issued_at,
                    courseSummary: saved.course_summary || '',
                    status: "Verified",
                };
                setCertificates(prev => prev.map(c => (c.id === editingCertId ? updated : c)));
                toast.success("Certificate updated successfully!");
            } else {
                saved = await issueCertificate(payload);
                const created = {
                    id: saved.id,
                    name: saved.student_name,
                    course: saved.course_name,
                    certId: saved.verification_code,
                    date: saved.issued_at,
                    courseSummary: saved.course_summary || '',
                    status: "Verified",
                };
                setCertificates(prev => [created, ...prev]);
                toast.success("Certificate issued successfully!");
            }

            setIsModalOpen(false);
            setEditingCertId(null);
            setActionMenuOpen(null);
            setViewCert(null);
            setFormData({
                firstName: "", lastName: "", courseOfStudy: "", courseSummary: "",
                yearOfStudy: "2025", issuanceDate: "", certificateNumber: ""
            });
        } catch (err: any) {
            console.error("Error saving certificate:", err);
            toast.error(err?.message || "An error occurred while saving.");
        }
    };

    // NEW: Delete Confirmation
    const openDeleteModal = (cert: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setCertToDelete(cert);
        setDeleteConfirmText('');
        setDeleteModalOpen(true);
        setActionMenuOpen(null);
    };

    const confirmAndDelete = async () => {
        if (!certToDelete || deleteConfirmText.toLowerCase() !== 'delete') return;

        try {
            await deleteCertificate(certToDelete.id);
            setCertificates(prev => prev.filter(c => c.id !== certToDelete.id));
            toast.success("Certificate deleted successfully.");
        } catch (err: any) {
            console.error("Delete error:", err);
            toast.error(err?.message || "Failed to delete certificate");
        } finally {
            setDeleteModalOpen(false);
            setCertToDelete(null);
            setDeleteConfirmText('');
        }
    };

    const generatePDF = async (cert: any) => {
        setIsGeneratingPdf(true);
        try {
            const loadImageAsDataUrl = (url: string, mime: string = 'image/png') => new Promise<string>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        const c = document.createElement('canvas');
                        c.width = img.naturalWidth;
                        c.height = img.naturalHeight;
                        const ctx = c.getContext('2d');
                        if (!ctx) return reject(new Error('Canvas context unavailable'));
                        ctx.drawImage(img, 0, 0);
                        const data = c.toDataURL(mime);
                        resolve(data);
                    } catch (err) {
                        reject(err);
                    }
                };
                img.onerror = (e) => reject(e);
                img.src = url;
            });

            let bg: string;
            try {
                bg = await loadImageAsDataUrl(CERT_BG_URL, 'image/jpeg');
            } catch (e) {
                console.error('Failed to load certificate background image via image loader', e);
                // As a fallback, try fetch->blob->dataURL (may fail due to CORS)
                try {
                    const r = await fetch(CERT_BG_URL);
                    const b = await r.blob();
                    bg = await new Promise<string>((resolve) => { const fr = new FileReader(); fr.onloadend = () => resolve(fr.result as string); fr.readAsDataURL(b); });
                } catch (e2) {
                    console.warn('Fallback fetch also failed for background image', e2);
                    // Final fallback: if the certificate DOM is visible, render it with html2canvas
                    if (certificateRef?.current) {
                        try {
                            const canvas = await html2canvas(certificateRef.current as HTMLElement, { scale: 2 });
                            bg = canvas.toDataURL('image/jpeg', 1.0);
                        } catch (e3) {
                            console.error('html2canvas fallback also failed', e3);
                            throw new Error('Failed to load certificate background image');
                        }
                    } else {
                        throw new Error('Failed to load certificate background image');
                    }
                }
            }
            const img = new Image();
            img.src = bg;
            await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = (e) => rej(e); });
            const { naturalWidth: w, naturalHeight: h } = img;
            const k = Math.min(w / 842, h / 595);
            const px = (n: number) => Math.round(n * k);
            const cx = (n: number) => Math.round((n * w) / 842);
            const cy = (n: number) => Math.round((n * h) / 595);

            const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] });
            pdf.addImage(bg, 'JPEG', 0, 0, w, h, undefined, 'NONE');
            pdf.setFont('times', 'bold');
            let sName = px(50);
            const maxW = cx(606);
            while (sName > px(22)) { pdf.setFontSize(sName); if (pdf.getTextWidth(cert.name || '') <= maxW) break; sName -= 1; }
            pdf.setFontSize(sName);
            pdf.setTextColor('#111827');
            pdf.text(cert.name, w / 2, cy(266), { align: 'center' });
            pdf.setFont('times', 'normal');
            let sCourse = px(30);
            pdf.setFontSize(sCourse);
            pdf.setTextColor('#1e3a8a');
            const courseText = (cert.course || '').toUpperCase();
            let courseLines = pdf.splitTextToSize(courseText, maxW);
            if (courseLines.length > 2) courseLines = courseLines.slice(0, 2);
            if (courseLines.length > 1) { sCourse = px(26); pdf.setFontSize(sCourse); }
            const baseY = cy(343);
            const gap = px(26);
            courseLines.forEach((ln: string, i: number) => {
                pdf.text(ln, Math.round(w / 2), baseY + i * gap, { align: 'center' });
            });
            pdf.setFont('times', 'bold');
            pdf.setFontSize(px(14));
            pdf.setTextColor('#111827');
            pdf.text(cert.date, cx(84), cy(520));
            pdf.setFont('courier', 'normal');
            let sId = px(17);
            const maxIdW = cx(260);
            while (sId > px(10)) { pdf.setFontSize(sId); if (pdf.getTextWidth(cert.certId || '') <= maxIdW) break; sId -= 1; }
            pdf.setFontSize(sId);
            pdf.setTextColor('#6b7280');
            pdf.text(cert.certId, w - cx(50), cy(74), { align: 'right' });
            const qrPayload = buildQrPayload(cert);
            let qrData: string | null = null;
            try {
                qrData = await QRCode.toDataURL(qrPayload, {
                    margin: 0,
                    errorCorrectionLevel: 'H',
                });
            } catch (e) {
                console.warn('QR generation failed, skipping QR:', e);
                qrData = null;
            }
            if (qrData) {
                try {
                    pdf.addImage(qrData, 'PNG', w - cx(450), cy(459), cx(90), cy(85));
                } catch (e) {
                    console.warn('Failed to add QR image to PDF, skipping QR:', e);
                }
            }
            pdf.save(`Certificate-${cert.certId}.pdf`);
        } catch (err) {
            console.error('PDF Gen Error', err);
            const msg = err instanceof Error ? err.message : String(err);
            toast.error(`Failed to generate PDF: ${msg}`);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const generatePDFForCert = async (cert: any) => {
        setViewCert(cert);
        await new Promise(r => setTimeout(r, 300));
        await generatePDF(cert);
    };

    const handleToggleAll = (checked: boolean) => {
        setSelectAll(checked);
        setSelectedIds(checked ? filteredCertificates.map(c => c.id) : []);
    };

    const handleToggleOne = (id: number, checked: boolean) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    };

    const handleBulkDownload = async () => {
        const items = selectedIds.length > 0
            ? certificates.filter(c => selectedIds.includes(c.id))
            : filteredCertificates;
        for (const item of items) {
            await generatePDFForCert(item);
        }
        setViewCert(null);
    };

    const handleImportClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowed = [".csv", ".xlsx", ".xls"];
        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
        if (!allowed.includes(ext)) {
            toast.error("Please upload a CSV or Excel file.");
            e.target.value = "";
            return;
        }

        try {
            toast.info("Uploading...");
            await importCertificatesCsv(file);
            toast.success("Imported successfully!");
            const data = await fetchCertificates();
            const mapped = (data.certificates || []).map((c: any) => ({
                id: c.id,
                name: c.student_name,
                course: c.course_name,
                certId: c.verification_code,
                date: c.issued_at,
                status: "Verified"
            }));
            setCertificates(mapped);
            setTotalCertificates(mapped.length);
        } catch (err: any) {
            toast.error(err?.message || "Import failed");
        } finally {
            e.target.value = "";
        }
    };

    const handleExport = () => {
        const headers = ["Name", "Course", "Certificate ID", "Date", "Status"];
        const rows = certificates.map(c => [c.name, c.course, c.certId, c.date, c.status]);
        const csv = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(r => `"${r.join('","')}"`).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", "certificates.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleActionMenu = (id: number) => {
        setActionMenuOpen(prev => prev === id ? null : id);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.action-menu-container')) {
                setActionMenuOpen(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleEditClick = (cert: any) => {
        const [first, ...last] = cert.name.split(' ');
        setFormData({
            firstName: first || '',
            lastName: last.join(' '),
            courseOfStudy: cert.course,
            courseSummary: cert.courseSummary || '',
            yearOfStudy: '2025',
            issuanceDate: (typeof cert.date === 'string' ? String(cert.date).slice(0, 10) : ''),
            certificateNumber: cert.certId
        });
        setEditingCertId(cert.id);
        setIsModalOpen(true);
        setActionMenuOpen(null);
    };

    const handleOpenIssueModal = () => {
        setFormData({
            firstName: '', lastName: '', courseOfStudy: '', courseSummary: '',
            yearOfStudy: '2025', issuanceDate: '', certificateNumber: ''
        });
        setEditingCertId(null);
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-6">
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx,.xls" className="hidden" />

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Certificates</h2>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">Manage and verify issued certificates.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                        <input 
                            type="text" 
                            placeholder="Search certificates..." 
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full md:w-64 focus:ring-primary focus:border-primary bg-white dark:bg-gray-700"
                        />
                    </div>
                    <button onClick={handleImportClick} className="bg-white dark:bg-gray-800 border border-primary text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center gap-2">
                        <FileUp className="w-4 h-4" /> Import CSV
                    </button>
                    <button onClick={handleOpenIssueModal} className="bg-primary hover:bg-indigo-800 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Issue Certificate
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600">
                            <FileDown className="w-4 h-4" /> Export
                        </button>
                        {/* <button onClick={handleBulkDownload} className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-primary text-primary rounded-md text-sm font-medium hover:bg-indigo-50 dark:hover:bg-gray-700">
                            Download Selected
                        </button>
                        <button onClick={() => setReloadKey(k => k + 1)} className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-primary text-primary rounded-md text-sm font-medium hover:bg-indigo-50 dark:hover:bg-gray-700">
                            Refresh
                        </button> */}
                        <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                            <Filter className="w-3 h-3" /> Filters
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleBulkDownload}
                            disabled={selectedIds.length < 2}
                            className={`px-3 py-1.5 border rounded-md text-xs font-semibold ${
                                selectedIds.length >= 2
                                    ? 'border-primary text-white bg-primary hover:bg-indigo-900 dark:hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                            }`}
                        >
                            Download Selected
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm">
                            <option value="All">Status: All</option>
                            <option value="Verified">Verified</option>
                            <option value="Revoked">Revoked</option>
                        </select>
                        <select value={courseFilter} onChange={(e) => { setCourseFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm">
                            <option value="All">Course: All</option>
                            <option value="Full Stack Web Dev">Full Stack Web Dev</option>
                            <option value="Data Analytics">Data Analytics</option>
                            <option value="Cybersecurity">Cybersecurity</option>
                        </select>
                        <input type="date" value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm" />
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
                        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs uppercase font-bold">
                            <tr>
                                <th className="px-6 py-3"><input type="checkbox" checked={selectAll} onChange={(e) => handleToggleAll(e.target.checked)} className="rounded" /></th>
                                <th className="px-6 py-3">Name</th>
                                <th className="px-6 py-3">Course</th>
                                <th className="px-6 py-3">Certificate ID</th>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {currentItems.length > 0 ? currentItems.map((cert, idx) => (
                                <tr key={`${cert?.id ?? cert?.certId ?? cert?.name ?? 'row'}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4"><input type="checkbox" checked={selectedIds.includes(cert.id)} onChange={(e) => handleToggleOne(cert.id, e.target.checked)} className="rounded" /></td>
                                    <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">{cert.name}</td>
                                    <td className="px-6 py-4">{cert.course}</td>
                                    <td className="px-6 py-4 font-mono text-xs">{cert.certId}</td>
                                    <td className="px-6 py-4">{cert.date}</td>
                                    <td className="px-6 py-4 action-menu-container relative">
                                        <button onClick={(e) => { e.stopPropagation(); toggleActionMenu(cert.id); }} className="p-1">
                                            <MoreHorizontal className="w-5 h-5" />
                                        </button>
                                        {actionMenuOpen === cert.id && (
                                            <div className="absolute right-8 top-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border z-50">
                                                <div className="py-1">
                                                    <button onClick={() => { setViewCert(cert); setActionMenuOpen(null); }} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
                                                        <Eye className="w-4 h-4" /> View
                                                    </button>
                                                    <button onClick={() => handleEditClick(cert)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
                                                        <Edit className="w-4 h-4" /> Edit
                                                    </button>
                                                    <button onClick={(e) => openDeleteModal(cert, e)} className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left font-medium">
                                                        <Trash className="w-4 h-4" /> Delete
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="text-center py-8 text-gray-500">No certificates found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 border-t flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50">
                    <span>
                        {filteredCertificates.length === 0
                            ? 'Showing 0-0 of 0'
                            : `Showing ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredCertificates.length)} of ${filteredCertificates.length}`}
                    </span>
                    <div className="flex gap-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
                        {Array.from({ length: totalPagesComputed }, (_, i) => i + 1).map(p => (
                            <button key={p} onClick={() => handlePageChange(p)} className={`px-3 py-1 rounded ${currentPage === p ? 'bg-primary text-white' : 'border'}`}>{p}</button>
                        ))}
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPagesComputed} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                    </div>
                </div>
            </div>

            {/* DELETE CONFIRMATION MODAL – WITH X CLOSE BUTTON */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 border relative">
                        {/* X Close Button */}
                        <button
                            onClick={() => {
                                setDeleteModalOpen(false);
                                setCertToDelete(null);
                                setDeleteConfirmText('');
                            }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="flex items-center gap-3 text-red-600 mb-4">
                            <AlertCircle className="w-8 h-8" />
                            <h3 className="text-xl font-bold">Delete Certificate?</h3>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 mb-6">
                            Are you sure you want to delete the certificate for <strong>{certToDelete?.name}</strong>?<br />
                            <span className="font-bold text-red-600">This action cannot be undone.</span>
                        </p>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Type <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">delete</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="delete"
                                autoFocus
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setDeleteModalOpen(false);
                                    setCertToDelete(null);
                                    setDeleteConfirmText('');
                                }}
                                className="px-5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAndDelete}
                                disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                            >
                                <Trash className="w-4 h-4" /> Proceed & Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Certificate Modal */}
            {viewCert && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-5xl shadow-2xl p-6 border max-h-[95vh] overflow-auto">
                        <div className="flex justify-between items-center mb-4 pb-2 border-b">
                            <h3 className="text-lg font-bold flex items-center gap-2"><Award className="w-5 h-5 text-yellow-600" /> Certificate Preview</h3>
                            <div className="flex gap-3">
                                <button onClick={() => generatePDF(viewCert)} disabled={isGeneratingPdf} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-800 flex items-center gap-2 disabled:opacity-50">
                                    {isGeneratingPdf ? 'Generating...' : <><Download className="w-4 h-4" /> Download PDF</>}
                                </button>
                                <button onClick={() => setViewCert(null)}><X className="w-6 h-6" /></button>
                            </div>
                        </div>
                        <div className="flex-grow overflow-auto bg-gray-200 dark:bg-gray-900 p-8 rounded-lg flex items-center justify-center">
                            <div ref={certificateRef} className="w-[842px] h-[595px] relative shadow-2xl bg-white" style={{ backgroundImage: 'url(/certificate-bg.jpg)', backgroundSize: '100% 100%' }}>
                                <div className="relative z-10 w-full h-full text-center px-0">
                                    <h2 className="font-serif font-bold text-gray-900 capitalize absolute left-1/2 -translate-x-1/2" style={{ top: '217px', fontSize: `${getFitSize(viewCert.name, 34, 22, 38)}px` }}>
                                        {viewCert.name}
                                    </h2>
                                    <h3 className="font-medium text-indigo-900 uppercase absolute left-1/2 -translate-x-1/2 max-w-[72%] text-center whitespace-normal break-words" style={{ top: '320px', fontSize: `${getFitSize(viewCert.course, 28, 18, 32)}px` }}>
                                        {viewCert.course}
                                    </h3>
                                    <div className="absolute" style={{ top: '512px', left: '88px' }}>
                                        <p className="text-base text-gray-800 font-bold">{viewCert.date}</p>
                                    </div>
                                    <div className="absolute" style={{ top: '74px', right: '40px' }}>
                                        <span className="font-mono text-gray-800" style={{ fontSize: `${getFitSizeNarrow(viewCert.certId, 14, 10, 16)}px` }}>{viewCert.certId}</span>
                                    </div>
                                    <div className="absolute" style={{ top: '459px', right: '340px', width: '90px', height: '85px' }}>
                                        <QRCodeCanvas
                                            value={buildQrPayload(viewCert)}
                                            size={90}
                                            level="H"
                                            includeMargin={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Issue/Edit Modal - FULLY RESTORED */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border">
                        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{editingCertId ? 'Update Certificate' : 'Issue Certificate'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-8">
                            {!editingCertId && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">To issue bulk certificates you can import the database below</p>
                                    <a href="#" className="text-xs text-primary hover:underline mb-4 block font-semibold">Download Sample CSV File</a>
                                    <button onClick={handleImportClick} className="flex items-center gap-2 px-4 py-2 border border-primary text-primary dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-md text-sm font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 shadow-sm">
                                        <FileUp className="w-4 h-4" /> Import CSV File
                                    </button>
                                </div>
                            )}

                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Student Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {['First Name', 'Last Name', 'Course of Study', 'Course Summary', 'Year of Study', 'Issuance Date', 'Certificate Number'].map(label => {
                                    const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('coursesummary', 'courseSummary').replace('yearofstudy', 'yearOfStudy').replace('issuancedate', 'issuanceDate').replace('certificatenumber', 'certificateNumber');
                                    return (
                                        <div key={label}>
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} {label !== 'Certificate Number' && '*'}</label>
                                            <input 
                                                type={label.includes('Date') ? 'date' : 'text'}
                                                name={name} 
                                                value={(formData as any)[name]} 
                                                onChange={handleInputChange} 
                                                required={label !== 'Certificate Number'}
                                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" 
                                                placeholder={`Enter ${label}`} 
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-8">
                                <button onClick={handleSaveCertificate} className="w-fit bg-primary text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-800 shadow-md flex items-center gap-2">
                                    {editingCertId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    {editingCertId ? 'Update Certificate' : 'Issue Certificate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Certificates;





// import React, { useState, useRef, useEffect } from 'react';
// import { Plus, Download, Search, Filter, MoreHorizontal, X, Eye, Trash, FileUp, FileDown, Edit, Award, Save } from 'lucide-react';
// import { jsPDF } from 'jspdf';
// import html2canvas from 'html2canvas';
// import { editCertificate, fetchCertificates, importCertificatesCsv, issueCertificate, deleteCertificate } from "../../services/dashboardService";
// import { toast } from 'react-toastify';
// import api from '@/services/api';


// export const Certificates: React.FC = () => {
//     const [isModalOpen, setIsModalOpen] = useState(false);
//     const [viewCert, setViewCert] = useState<any | null>(null);
//     const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
//     const [searchQuery, setSearchQuery] = useState('');
//     const [currentPage, setCurrentPage] = useState(1);
//     const [showFilters, setShowFilters] = useState(false);
//     const [itemsPerPage] = useState(5);
//     const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
//     const [selectedIds, setSelectedIds] = useState<number[]>([]);
//     const [selectAll, setSelectAll] = useState(false);
//     const [certificates, setCertificates] = useState<any[]>([]);
// const [totalCertificates, setTotalCertificates] = useState(0);
// const [totalPages, setTotalPages] = useState(0);
    
//     // Edit State
//     const [editingCertId, setEditingCertId] = useState<number | null>(null);

//     // Filters
//     const [statusFilter, setStatusFilter] = useState('All');
//     const [courseFilter, setCourseFilter] = useState('All');
//     const [dateFilter, setDateFilter] = useState('');

//     const fileInputRef = useRef<HTMLInputElement>(null);
//     // Ref for the visual certificate inside the view modal
//     const certificateRef = useRef<HTMLDivElement>(null);

//     const [formData, setFormData] = useState({
//         firstName: '',
//         lastName: '',
//         courseOfStudy: '',
//         courseSummary: '',
//         yearOfStudy: '2025',
//         issuanceDate: '',
//         certificateNumber: ''
//     });

//     // const [certificates, setCertificates] = useState([
//     //     { id: 1, name: 'John Baker', course: 'Full Stack Web Dev', certId: 'SCVS-240590', date: '2025-10-23', status: 'Verified' },
//     //     { id: 2, name: 'Anna Mitchell', course: 'Full Stack Web Dev', certId: 'SCVS-240591', date: '2025-10-23', status: 'Verified' },
//     //     { id: 3, name: 'John Doe', course: 'Full Stack Web Dev', certId: 'SCVS-240592', date: '2025-10-23', status: 'Revoked' },
//     //     { id: 4, name: 'Jane Smith', course: 'Data Analytics', certId: 'SCVS-240593', date: '2025-10-23', status: 'Verified' },
//     //     { id: 5, name: 'Mike Johnson', course: 'Full Stack Web Dev', certId: 'SCVS-240594', date: '2025-10-23', status: 'Verified' },
//     //     { id: 6, name: 'Sarah Connor', course: 'Cybersecurity', certId: 'SCVS-240595', date: '2025-10-23', status: 'Verified' },
//     //     { id: 7, name: 'Tom Riddle', course: 'Dark Arts Defense', certId: 'SCVS-240596', date: '2025-10-24', status: 'Verified' },
//     //     { id: 8, name: 'Harry Potter', course: 'Flying Class', certId: 'SCVS-240597', date: '2025-10-24', status: 'Verified' },
//     // ]);

//     const filteredCertificates = certificates.filter(cert => {
//         const matchesSearch = cert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//                               cert.certId.toLowerCase().includes(searchQuery.toLowerCase()) ||
//                               cert.course.toLowerCase().includes(searchQuery.toLowerCase());
        
//         const matchesStatus = statusFilter === 'All' || cert.status === statusFilter;
//         const matchesCourse = courseFilter === 'All' || cert.course === courseFilter;
//         const matchesDate = dateFilter === '' || cert.date === dateFilter;

//         return matchesSearch && matchesStatus && matchesCourse && matchesDate;
//     });

//     const indexOfLastItem = currentPage * itemsPerPage;
//     const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//     const currentItems = filteredCertificates.slice(indexOfFirstItem, indexOfLastItem);
//     // const totalPages = Math.ceil(filteredCertificates.length / itemsPerPage);


//     useEffect(() => {
//     const loadCertificates = async () => {
//         try {
//             const data = await fetchCertificates(currentPage, itemsPerPage);
//             setCertificates(data.items.map((c: any) => ({
//                 id: c.id,
//                 name: c.student_name,
//                 course: c.course_name,
//                 certId: c.verification_code,
//                 date: c.issued_at,
//                 status: "Verified", // or dynamically decide if status exists
//             })));
//             setTotalCertificates(data.total);
//             setTotalPages(data.pages);
//         } catch (err) {
//             console.error(err);
//         }
//     };

//     loadCertificates();
// }, [currentPage, itemsPerPage]);


//     const getFitSize = (t: string, base: number, min: number, max: number) => {
//         const len = t ? t.length : 0;
//         let s = base;
//         if (len > 28) s = base - 10;
//         else if (len > 22) s = base - 8;
//         else if (len > 16) s = base - 6;
//         else if (len > 12) s = base - 4;
//         if (s < min) s = min;
//         if (s > max) s = max;
//         return s;
//     };

//     const handlePageChange = (newPage: number) => {
//         if (newPage >= 1 && newPage <= totalPages) {
//             setCurrentPage(newPage);
//         }
//     };

//     const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//         const { name, value } = e.target;
//         setFormData(prev => ({ ...prev, [name]: value }));
//     };

//     // const handleSaveCertificate = () => {
//     //     if (!formData.firstName || !formData.lastName || !formData.courseOfStudy) {
//     //         alert("Please fill in the required fields.");
//     //         return;
//     //     }
//     //     const d = formData.issuanceDate;
//     //     const today = new Date();
//     //     const isoToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString().split('T')[0];
//     //     const isIso = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d);
//     //     const dd = isIso ? new Date(d) : new Date('invalid');
//     //     const notFuture = isIso && d <= isoToday;
//     //     const notTooOld = isIso && d >= '2000-01-01';
//     //     if (!isIso || isNaN(dd.getTime()) || !notFuture || !notTooOld) {
//     //         alert("Enter a valid issuance date (YYYY-MM-DD), not in the future.");
//     //         return;
//     //     }

//     //     const certData = {
//     //         id: editingCertId || Date.now(), 
//     //         name: `${formData.firstName} ${formData.lastName}`,
//     //         course: formData.courseOfStudy,
//     //         certId: formData.certificateNumber || `SCVS-${Math.floor(Math.random() * 100000)}`,
//     //         date: formData.issuanceDate,
//     //         status: 'Verified'
//     //     };

//     //     if (editingCertId) {
//     //         // Update existing
//     //         setCertificates(prev => prev.map(c => c.id === editingCertId ? certData : c));
//     //         alert("Certificate updated successfully!");
//     //     } else {
//     //         // Create new
//     //         setCertificates([certData, ...certificates]);
//     //         alert("Certificate issued successfully!");
//     //     }

//     //     setIsModalOpen(false);
//     //     setEditingCertId(null);
//     //     setFormData({
//     //         firstName: '', lastName: '', courseOfStudy: '', courseSummary: '',
//     //         yearOfStudy: '2025', issuanceDate: '', certificateNumber: ''
//     //     });
//     // };


// const handleSaveCertificate = async () => {
//   if (!formData.firstName || !formData.lastName || !formData.courseOfStudy) {
//     alert("Please fill in the required fields.");
//     return;
//   }

//   // Validate issuance date
//   const d = formData.issuanceDate;
//   const today = new Date();
//   const isoToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
//     .toISOString()
//     .split("T")[0];
//   const isIso = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d);
//   const dd = isIso ? new Date(d) : new Date("invalid");
//   const notFuture = isIso && d <= isoToday;
//   const notTooOld = isIso && d >= "2000-01-01";

//   if (!isIso || isNaN(dd.getTime()) || !notFuture || !notTooOld) {
//     alert("Enter a valid issuance date (YYYY-MM-DD), not in the future.");
//     return;
//   }

//   // Map formData to backend payload
//   const payload = {
//     first_name: formData.firstName.trim(),
//     last_name: formData.lastName.trim(),
//     course_name: formData.courseOfStudy.trim(),
//     course_summary: formData.courseSummary.trim(),
//     year_of_study: formData.yearOfStudy,
//     issuance_date: formData.issuanceDate
//   };

//   try {
//     let saved;
//     if (editingCertId) {
//       // Update certificate
//       saved = await editCertificate(editingCertId, payload);
//       setCertificates(prev =>
//         prev.map(c => (c.id === editingCertId ? saved : c))
//       );
//       alert("Certificate updated successfully!");
//     } else {
//       // Issue new certificate
//       saved = await issueCertificate(payload);
//       setCertificates(prev => [saved, ...prev]);
//       alert("Certificate issued successfully!");
//     }

//     // Cleanup
//     setIsModalOpen(false);
//     setEditingCertId(null);
//     setFormData({
//       firstName: "",
//       lastName: "",
//       courseOfStudy: "",
//       courseSummary: "",
//       yearOfStudy: "2025",
//       issuanceDate: "",
//       certificateNumber: ""
//     });
//   } catch (err: any) {
//     console.error("Error saving certificate:", err);
//     alert(err || "An error occurred while saving.");
//   }
// };


//     const handleDelete = async (id: number, e: React.MouseEvent) => {
//     e.stopPropagation();

//     if (!window.confirm("Are you sure you want to delete this certificate? This action cannot be undone.")) {
//         return;
//     }

//     try {
//         await deleteCertificate(id);
//         setCertificates(prev => prev.filter(c => c.id !== id));
//         toast.success("Certificate deleted successfully.");
//     } catch (err: any) {
//         console.error("Delete error:", err);
//         toast.error(err?.message || "Failed to delete certificate");
//     } finally {
//         setActionMenuOpen(null);
//     }
//     };


//     const generatePDF = async (cert: any) => {
//         setIsGeneratingPdf(true);
//         try {
//             const toDataUrl = async (url: string) => {
//                 const r = await fetch(url);
//                 const b = await r.blob();
//                 return await new Promise<string>((resolve) => { const fr = new FileReader(); fr.onloadend = () => resolve(fr.result as string); fr.readAsDataURL(b); });
//             };
//             const bg = await toDataUrl('/public/certificate-bg.jpg');
//             const dims = await new Promise<{ w: number; h: number }>((resolve) => { const i = new Image(); i.onload = () => resolve({ w: i.naturalWidth, h: i.naturalHeight }); i.src = bg; });
//             const w = dims.w, h = dims.h;
//             const k = Math.min(w / 842, h / 595);
//             const px = (n: number) => Math.round(n * k);
//             const cx = (n: number) => Math.round((n * w) / 842);
//             const cy = (n: number) => Math.round((n * h) / 595);
//             const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] });
//             pdf.addImage(bg, 'JPEG', 0, 0, w, h, undefined, 'FAST');
//             pdf.setFont('times', 'bold');
//             pdf.setFontSize(px(getFitSize(cert.name, 50, 40, 40)));
//             pdf.setTextColor('#111827');
//             pdf.text(cert.name, Math.round(w / 2), cy(266), { align: 'center' });
//             pdf.setFont('times', 'normal');
//             pdf.setFontSize(px(getFitSize(cert.course, 32, 22, 36)));
//             pdf.setTextColor('#1e3a8a');
//             pdf.text((cert.course || '').toUpperCase(), Math.round(w / 2), cy(343), { align: 'center' });
//             pdf.setFont('times', 'bold');
//             pdf.setFontSize(px(14));
//             pdf.setTextColor('#111827');
//             pdf.text(cert.date, cx(84), cy(520));
//             pdf.setFont('courier', 'normal');
//             pdf.setFontSize(px(17));
//             pdf.setTextColor('#6b7280');
//             pdf.text(cert.certId, w - cx(89), cy(90), { align: 'right' });
//             pdf.save(`Certificate-${cert.certId}.pdf`);
//         } catch (err) {
//             console.error('PDF Gen Error', err);
//             alert('Failed to generate PDF');
//         } finally {
//             setIsGeneratingPdf(false);
//         }
//     };

//     const generatePDFForCert = async (cert: any) => {
//         setViewCert(cert);
//         await new Promise((r) => setTimeout(r, 300));
//         await generatePDF(cert);
//     };

//     const handleToggleAll = (checked: boolean) => {
//         setSelectAll(checked);
//         setSelectedIds(checked ? filteredCertificates.map((c) => c.id) : []);
//     };

//     const handleToggleOne = (id: number, checked: boolean) => {
//         setSelectedIds((prev) => {
//             const s = new Set(prev);
//             if (checked) s.add(id); else s.delete(id);
//             return Array.from(s);
//         });
//     };

//     const handleBulkDownload = async () => {
//         const items = selectedIds.length > 0
//             ? certificates.filter((c) => selectedIds.includes(c.id))
//             : filteredCertificates;
//         for (const item of items) {
//             await generatePDFForCert(item);
//         }
//         setViewCert(null);
//     };

//     const handleImportClick = () => {
//         if (fileInputRef.current) fileInputRef.current.click();
//     };

//     // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     //     const file = e.target.files?.[0];
//     //     if (file) {
//     //         if (file.type !== "text/csv") {
//     //             alert("Please upload a valid CSV file.");
//     //             return;
//     //         }
//     //         alert(`File "${file.name}" imported successfully! (Mock)`);
//     //     }
//     // };

// //     const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
// //     const file = e.target.files?.[0];
// //     if (!file) return;

// //     // Validate CSV
// //     if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
// //         // toast.error("Please upload a valid CSV file.");
// //         return;
// //     }

// //     try {
// //         // toast.info("Uploading CSV...");

// //         const response = await importCertificatesCsv(file);

// //         // toast.success("CSV imported successfully!");
// //         console.log("Import API Response:", response);

// //         // OPTIONAL: refresh the list after import
// //         // loadCertificates();

// //     } catch (error: any) {
// //         // toast.error(error || "CSV import failed");
// //     } finally {
// //         // Allow re-uploading the same file
// //         e.target.value = "";
// //     }
// // };

// const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
//   const file = e.target.files?.[0];
//   console.log("[IMPORT] file input changed", { file, fileType: file?.type, fileName: file?.name });

//   if (!file) {
//     console.log("[IMPORT] no file selected");
//     return;
//   }

//   const allowedExtensions = [".csv", ".xlsx", ".xls"];
// const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));

// if (!allowedExtensions.includes(fileExtension)) {
//     toast.error("Please upload a CSV or Excel file.");
//     console.log("[IMPORT] rejected file type", file.type);
//     e.target.value = "";
//     return;
// }

//   // sanity: ensure fileInputRef exists
//   console.log("[IMPORT] fileInputRef present?", !!fileInputRef.current);

//   try {
//     toast.info("Uploading CSV (axios)...");
//     console.log("[IMPORT] calling importCertificatesCsv (axios) with file:", file);

//     // Show current api baseURL for debugging
//     console.log("[IMPORT] axios baseURL:", api.defaults?.baseURL);

//     const res = await importCertificatesCsv(file);

//     console.log("[IMPORT] importCertificatesCsv response:", res);
//     toast.success("CSV imported successfully (axios)!");
//   } catch (axiosErr: any) {
//     console.error("[IMPORT] importCertificatesCsv ERROR:", axiosErr);
//     // Give more feedback if axios produced an error response
//     const errMsg = axiosErr?.response?.data || axiosErr?.message || axiosErr;
//     toast.error(`Axios import failed: ${errMsg}`);
//   }

//   // --- fallback using fetch so we can observe raw network request easily ---
//   try {
//     // toast.info("Uploading CSV (fetch fallback)...");
//     const base = api.defaults?.baseURL || ""; // if empty, fetch will use relative path
//     const url = base + "/certificate/certificates/import";
//     console.log("[IMPORT] fetch fallback will POST to:", url);

//     const fd = new FormData();
//     fd.append("file", file);

//     const fetchRes = await fetch(url, {
//       method: "POST",
//       body: fd,
//       // don't set Content-Type header; browser will set boundary
//     });

//     console.log("[IMPORT] fetch response status:", fetchRes.status, fetchRes);
//     const fetchText = await fetchRes.text();
//     console.log("[IMPORT] fetch response body (text):", fetchText);

//     if (fetchRes.ok) {
//       toast.success("CSV imported successfully (fetch)!");
//     } else {
//       toast.error(`Fetch import failed: ${fetchRes.status}`);
//     }
//   } catch (fetchErr) {
//     console.error("[IMPORT] fetch fallback ERROR:", fetchErr);
//     toast.error("Fetch import failed - see console");
//   } finally {
//     e.target.value = ""; // Reset input so same file can be re-uploaded
//   }
// };

//     const handleExport = () => {
//         const headers = ["Name", "Course", "Certificate ID", "Date", "Status"];
//         const rows = certificates.map(c => [c.name, c.course, c.certId, c.date, c.status]);
//         const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
//         const encodedUri = encodeURI(csvContent);
//         const link = document.createElement("a");
//         link.setAttribute("href", encodedUri);
//         link.setAttribute("download", "certificates.csv");
//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);
//     };

//     const toggleActionMenu = (id: number) => {
//         setActionMenuOpen(actionMenuOpen === id ? null : id);
//     };

//     useEffect(() => {
//         const handleClickOutside = (event: MouseEvent) => {
//             if (!(event.target as Element).closest('.action-menu-container')) {
//                 setActionMenuOpen(null);
//             }
//         };
//         document.addEventListener('mousedown', handleClickOutside);
//         return () => document.removeEventListener('mousedown', handleClickOutside);
//     }, []);

//     const handleEditClick = (cert: any) => {
//         const [first, ...last] = cert.name.split(' ');
//         setFormData({
//             firstName: first,
//             lastName: last.join(' '),
//             courseOfStudy: cert.course,
//             courseSummary: '',
//             yearOfStudy: '2025',
//             issuanceDate: cert.date,
//             certificateNumber: cert.certId
//         });
//         setEditingCertId(cert.id);
//         setIsModalOpen(true);
//         setActionMenuOpen(null);
//     };

//     const handleOpenIssueModal = () => {
//         setFormData({
//             firstName: '', lastName: '', courseOfStudy: '', courseSummary: '',
//             yearOfStudy: '2025', issuanceDate: '', certificateNumber: ''
//         });
//         setEditingCertId(null);
//         setIsModalOpen(true);
//     }

//   return (
//     <div className="space-y-6">
//       <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv" className="hidden" />

//       {/* Header Actions */}
//       <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
//         <div>
//             <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Certificates</h2>
//             <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">Manage and verify issued certificates.</p>
//         </div>
//         <div className="flex flex-col sm:flex-row gap-3">
//             <div className="relative">
//                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-4 h-4" />
//                 <input 
//                     type="text" 
//                     placeholder="Search certificates..." 
//                     value={searchQuery}
//                     onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
//                     className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full md:w-64 focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
//                 />
//             </div>
//             <button onClick={handleImportClick} className="bg-white dark:bg-gray-800 border border-primary text-primary dark:text-indigo-400 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2 whitespace-nowrap shadow-sm transition-colors">
//                 <FileUp className="w-4 h-4" /> Import CSV
//             </button>
//             <button onClick={handleOpenIssueModal} className="bg-darkblue hover:bg-indigo-900 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap shadow-sm">
//                 <Plus className="w-4 h-4" /> Issue Certificate
//             </button>
//             <input
//     type="file"
//     ref={fileInputRef}
//     // accept=".csv"
//     accept=".csv, .xlsx, .xls"

//     onChange={handleFileChange}
//     className="hidden"
// />

//         </div>
//       </div>

//       {/* Table Section */}
//       <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col min-h-[400px] transition-colors">
//         <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
//             <div className="flex items-center gap-2">
//                 <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
//                     <FileDown className="w-4 h-4" /> Export
//                 </button>
//                 <button 
//                     onClick={() => setShowFilters(!showFilters)}
//                     className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium transition-colors ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
//                 >
//                     <Filter className="w-3 h-3" /> Filters
//                 </button>
//             </div>
//         </div>

//         {showFilters && (
//             <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-3 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
//                 <select 
//                     value={statusFilter}
//                     onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
//                     className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700 font-medium"
//                 >
//                     <option value="All">Status: All</option>
//                     <option value="Verified">Verified</option>
//                     <option value="Revoked">Revoked</option>
//                 </select>
//                 <select 
//                     value={courseFilter}
//                     onChange={(e) => { setCourseFilter(e.target.value); setCurrentPage(1); }}
//                     className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700 font-medium"
//                 >
//                     <option value="All">Course: All</option>
//                     <option value="Full Stack Web Dev">Full Stack Web Dev</option>
//                     <option value="Data Analytics">Data Analytics</option>
//                     <option value="Cybersecurity">Cybersecurity</option>
//                 </select>
//                 <input 
//                     type="date"
//                     value={dateFilter}
//                     onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
//                     className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700 font-medium"
//                 />
//             </div>
//         )}

//         <div className="overflow-x-auto flex-grow">
//             <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300 min-w-[800px]">
//                 <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 uppercase text-xs font-bold">
//                     <tr>
//                         <th className="px-6 py-3 w-4">
//                             <input type="checkbox" className="rounded border-gray-400 text-primary focus:ring-primary bg-white" checked={selectAll} onChange={(e) => handleToggleAll(e.target.checked)} />
//                         </th>
//                         <th className="px-6 py-3">Name</th>
//                         <th className="px-6 py-3">Course</th>
//                         <th className="px-6 py-3">Certificate ID</th>
//                         <th className="px-6 py-3">Date</th>
//                         <th className="px-6 py-3">Status</th>
//                         <th className="px-6 py-3">Action</th>
//                     </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
//                     {currentItems.length > 0 ? (
//                         currentItems.map((cert) => (
//                         <tr key={cert.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 relative transition-colors">
//                             <td className="px-6 py-4">
//                                 <input type="checkbox" className="rounded border-gray-400 text-primary focus:ring-primary bg-white" checked={selectedIds.includes(cert.id)} onChange={(e) => handleToggleOne(cert.id, e.target.checked)} />
//                             </td>
//                             <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">{cert.name}</td>
//                             <td className="px-6 py-4 text-gray-800 dark:text-gray-300 font-medium">{cert.course}</td>
//                             <td className="px-6 py-4 font-mono text-xs text-gray-700 dark:text-gray-400 font-medium">{cert.certId}</td>
//                             <td className="px-6 py-4 text-gray-800 dark:text-gray-300">{cert.date}</td>
//                             <td className="px-6 py-4">
//                                 <span className={`px-2 py-1 rounded text-xs font-bold ${cert.status === 'Verified' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
//                                     {cert.status}
//                                 </span>
//                             </td>
//                             <td className="px-6 py-4 action-menu-container relative">
//                                 <button onClick={(e) => { e.stopPropagation(); toggleActionMenu(cert.id); }} className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white p-1">
//                                     <MoreHorizontal className="w-5 h-5" />
//                                 </button>
//                                 {actionMenuOpen === cert.id && (
//                                     <div className="absolute right-8 top-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20 animate-in fade-in zoom-in duration-200">
//                                         <div className="py-1">
//                                             <button 
//                                                 onClick={() => { setViewCert(cert); setActionMenuOpen(null); }} 
//                                                 className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium"
//                                             >
//                                                 <Eye className="w-4 h-4" /> View
//                                             </button>
//                                             <button 
//                                                 onClick={() => { handleEditClick(cert); }} 
//                                                 className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium"
//                                             >
//                                                 <Edit className="w-4 h-4" /> Edit
//                                             </button>
//                                             <button 
//                                                 onClick={(e) => handleDelete(cert.id, e)} 
//                                                 className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left font-medium"
//                                             >
//                                                 <Trash className="w-4 h-4" /> Delete
//                                             </button>
//                                         </div>
//                                     </div>
//                                 )}
//                             </td>
//                         </tr>
//                         ))) : (
//                         <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-600 dark:text-gray-400 font-medium">No certificates found matching criteria.</td></tr>
//                     )}
//                 </tbody>
//             </table>
//         </div>
//         <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
//             <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
//                 {/* Showing {filteredCertificates.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredCertificates.length)} of {filteredCertificates.length} entries */}
//                 Showing {certificates.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-
//     {Math.min(currentPage * itemsPerPage, totalCertificates)} of {totalCertificates} entries
//                 </span>
//             <div className="flex gap-2">
//                 <button onClick={handleBulkDownload} className="px-3 py-1 border border-primary text-primary rounded text-xs font-semibold hover:bg-blue-50 dark:hover:bg-gray-700">Download</button>
//                 <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-medium transition-colors">Previous</button>
//                 {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//                     <button key={page} onClick={() => handlePageChange(page)} className={`px-3 py-1 border rounded text-xs font-medium transition-colors ${currentPage === page ? 'bg-primary text-white border-primary' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}>{page}</button>
//                 ))}
//                 <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-medium transition-colors">Next</button>
//             </div>
//         </div>
//       </div>

//       {/* Visual Certificate Preview Modal */}
//       {viewCert && (
//         <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200 backdrop-blur-sm">
//              <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-5xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700 flex flex-col max-h-[95vh]">
//                  <div className="flex justify-between items-center mb-4 border-b border-gray-100 dark:border-gray-700 pb-2">
//                      <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
//                         <Award className="w-5 h-5 text-yellow-600" /> Certificate Preview
//                      </h3>
//                      <div className="flex gap-3">
//                          <button 
//                             onClick={() => generatePDF(viewCert)} 
//                             disabled={isGeneratingPdf}
//                             className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-800 flex items-center gap-2 shadow-sm disabled:opacity-50"
//                         >
//                             {isGeneratingPdf ? 'Generating...' : <><Download className="w-4 h-4" /> Download PDF</>}
//                          </button>
//                          <button onClick={() => setViewCert(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
//                              <X className="w-6 h-6" />
//                          </button>
//                      </div>
//                  </div>
                 
//                  {/* Visual Certificate Container */}
//                  <div className="flex-grow overflow-auto bg-gray-200 dark:bg-gray-900 p-8 rounded-lg flex items-center justify-center">
                     
//                      {/* 
//                         Use AUTHENTIC LOCAL IMAGE as background.
//                         Ensure your image is at public/public/certificate-bg.png 
//                      */}
//                      <div 
//                         ref={certificateRef} 
//                         className="w-[842px] h-[595px] relative shadow-2xl flex-shrink-0 bg-white"
//                         style={{
//                           backgroundImage: 'url(public/certificate-bg.jpg)',
//                           backgroundSize: '100% 100%',
//                           backgroundPosition: 'center',
//                           backgroundRepeat: 'no-repeat'
//                         }}
//                      >

//                        {/* Content Layer - Overlay Text on top of Image */}
//                       <div className="relative z-10 w-full h-full px-0 text-center">
                            
//                            {/* Student Name */}
//                            <h2 
//                                className="font-serif font-bold text-gray-900 capitalize absolute left-1/2 -translate-x-1/2 leading-tight max-w-[72%] text-center whitespace-nowrap"
//                                style={{ top: '237px', fontSize: getFitSize(viewCert.name, 34, 22, 38), fontFamily: "'Times New Roman', Times, serif" }}
//                            >
//                                {viewCert.name}
//                            </h2>
                            
//                            {/* Course */}
//                            <p className="hidden">For successfully completing training in</p>
//                            <h3 
//                                className="font-medium leading-snug text-indigo-900 uppercase tracking-normal absolute left-1/2 -translate-x-1/2 max-w-[72%] text-center whitespace-nowrap"
//                                style={{ top: '320px', fontSize: getFitSize(viewCert.course, 30, 20, 34), fontFamily: "'Times New Roman', Times, serif" }}
//                            >
//                                {viewCert.course}
//                            </h3>
                            
//                             {/* Date */}
//                             <div className="absolute" style={{ top: '512px', left: '88px' }}>
//                                 <p className="text-base text-gray-800 font-bold">{viewCert.date}</p>
//                             </div>

//                             {/* Cert ID */}
//                             <div className="absolute" style={{ top: '73px', right: '86px' }}>
//                                 <span className="font-mono text-sm text-gray-800">{viewCert.certId}</span>
//                             </div>

//                         </div>
//                      </div>
//                  </div>
//              </div>
//         </div>
//       )}

//       {/* Issue/Edit Modal */}
//       {isModalOpen && (
//           <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
//               <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
//                   <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
//                       <div className="flex items-center gap-2">
//                         <h3 className="text-xl font-bold text-gray-900 dark:text-white">{editingCertId ? 'Update Certificate' : 'Issue Certificate'}</h3>
//                       </div>
//                       <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
//                   </div>
//                   <div className="p-6 space-y-8">
//                        {!editingCertId && (
//                            <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
//                               <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">To issue bulk certificates you can import the database below</p>
//                               <a href="#" className="text-xs text-primary hover:underline mb-4 block font-semibold">Download Sample CSV File</a>
//                               <button onClick={handleImportClick} className="flex items-center gap-2 px-4 py-2 border border-primary text-primary dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-md text-sm font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors shadow-sm"><FileUp className="w-4 h-4" /> Import CSV File</button>
//                           </div>
//                        )}

//                       <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Student Details</h4>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                            {['First Name', 'Last Name', 'Course of Study', 'Course Summary', 'Year of Study', 'Issuance Date', 'Certificate Number'].map(label => {
//                                const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('coursesummary', 'courseSummary').replace('yearofstudy', 'yearOfStudy').replace('issuancedate', 'issuanceDate').replace('certificatenumber', 'certificateNumber');
//                                return (
//                                 <div key={label}>
//                                     <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} {label !== 'Certificate Number' && '*'}</label>
//                                     <input 
//                                         type={label.includes('Date') ? 'date' : 'text'}
//                                         name={name} 
//                                         //@ts-ignore
//                                         value={formData[name]} 
//                                         onChange={handleInputChange} 
//                                         required={label !== 'Certificate Number'}
//                                         min={label === 'Issuance Date' ? '2000-01-01' : undefined}
//                                         max={label === 'Issuance Date' ? new Date().toISOString().split('T')[0] : undefined}
//                                         className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400" 
//                                         placeholder={`Enter ${label}`} 
//                                     />
//                                 </div>
//                                );
//                            })}
//                       </div>
//                       <div className="mt-8">
//                           <button onClick={handleSaveCertificate} className="w-fit bg-darkblue text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-900 transition-all shadow-md flex items-center gap-2">
//                             {editingCertId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
//                             {editingCertId ? 'Update Certificate' : 'Issue Certificate'}
//                           </button>
//                       </div>
//                   </div>
//               </div>
//           </div>
//       )}
//     </div>
//   );
// };
